class HerculesBinFinder extends HTMLElement {
  connectedCallback() {
    this.launcher = this.querySelector('.hercules-bin-finder__launcher');
    this.dialog = this.querySelector('.hercules-bin-finder__dialog');
    this.body = this.querySelector('.hercules-bin-finder__body');
    this.sizeStep = this.querySelector('[data-step="size"]');
    this.resultStep = this.querySelector('[data-step="result"]');
    this.products = JSON.parse(this.querySelector('[data-finder-products]').textContent);
    this.state = { environment: null, size: null };
    this.drag = { startY: 0, currentY: 0, active: false };

    this.onClick = this.onClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', () => this.syncOpenState(false));

    const dragHandle = this.querySelector('[data-drag-handle]');
    dragHandle?.addEventListener('pointerdown', this.onPointerDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  onClick(event) {
    const launcher = event.target.closest('.hercules-bin-finder__launcher');
    const close = event.target.closest('[data-finder-close]');
    const environment = event.target.closest('[data-environment]');
    const size = event.target.closest('[data-size]');
    const restart = event.target.closest('[data-finder-restart]');

    if (launcher) this.open();
    if (close) this.close();
    if (environment) this.selectEnvironment(environment);
    if (size) this.selectSize(size);
    if (restart) this.reset();
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  open() {
    this.loadImages();
    this.dialog.showModal();
    this.syncOpenState(true);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  syncOpenState(isOpen) {
    this.launcher.setAttribute('aria-expanded', String(isOpen));
    this.dialog.style.transform = '';
    this.dialog.classList.remove('is-dragging');
  }

  loadImages() {
    this.querySelectorAll('img[data-lazy-src]').forEach((image) => {
      if (!image.src) image.src = image.dataset.lazySrc;
    });
  }

  selectEnvironment(button) {
    this.state.environment = button.dataset.environment;
    this.setPressed('[data-environment]', button);
    this.sizeStep.hidden = false;
    this.resultStep.hidden = true;
    this.state.size = null;
    this.setPressed('[data-size]', null);
    this.reveal(this.sizeStep);
  }

  selectSize(button) {
    this.state.size = button.dataset.size;
    this.setPressed('[data-size]', button);
    this.renderResult();
    this.resultStep.hidden = false;
    this.reveal(this.resultStep);
  }

  setPressed(selector, activeButton) {
    this.querySelectorAll(selector).forEach((button) => {
      button.setAttribute('aria-pressed', String(button === activeButton));
    });
  }

  renderResult() {
    const product = this.products[this.state.size];
    const sizeLabels = {
      15: 'Saco de 15 litros',
      30: 'Saco de 30 litros',
      50: 'Saco de 50 litros',
      100: 'Saco de 100 litros',
    };
    const environmentLabels = {
      bathroom: 'para banheiros e lavabos, com troca prática no dia a dia.',
      bedroom: 'para quartos e escritórios, sem excesso de material na lixeira.',
      kitchen: 'para cozinhas e lavanderias, equilibrando capacidade e resistência.',
      outdoor: 'para áreas externas e limpezas maiores, com espaço para mais volume.',
    };

    this.querySelector('[data-result-size]').textContent = sizeLabels[this.state.size];
    this.querySelector('[data-result-reason]').textContent = `Pelo tamanho e pelo ambiente escolhido, esta é a opção mais indicada ${environmentLabels[this.state.environment]}`;
    this.querySelector('[data-result-title]').textContent = product.title;
    this.querySelector('[data-result-price]').textContent = product.price || '';
    this.querySelector('[data-result-link]').href = product.url;

    const image = this.querySelector('[data-result-image]');
    image.src = product.image || '';
    image.alt = product.title;
  }

  reset() {
    this.state = { environment: null, size: null };
    this.setPressed('[data-environment]', null);
    this.setPressed('[data-size]', null);
    this.sizeStep.hidden = true;
    this.resultStep.hidden = true;
    this.dialog.classList.remove('is-expanded');
    this.body.scrollTo({ top: 0, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  reveal(element) {
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.dialog.classList.add('is-expanded');
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const bodyRect = this.body.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const top = this.body.scrollTop + elementRect.top - bodyRect.top;

        this.body.scrollTo({
          top: Math.max(0, top),
          behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onPointerDown(event) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    this.drag = { startY: event.clientY, currentY: event.clientY, active: true };
    event.currentTarget.setPointerCapture(event.pointerId);
    this.dialog.classList.add('is-dragging');
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (!this.drag.active) return;
    this.drag.currentY = event.clientY;
    const distance = Math.max(0, this.drag.currentY - this.drag.startY);
    this.dialog.style.transform = `translateY(${distance}px)`;
  }

  onPointerUp() {
    if (!this.drag.active) return;
    const distance = this.drag.currentY - this.drag.startY;
    this.drag.active = false;
    this.dialog.style.transform = '';
    this.dialog.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onPointerMove);

    if (distance > 100) {
      this.close();
    } else if (distance < -60) {
      this.dialog.classList.add('is-expanded');
    }
  }
}

if (!customElements.get('hercules-bin-finder')) {
  customElements.define('hercules-bin-finder', HerculesBinFinder);
}
